import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Label, EmptyState } from "../components/ui-kit";
import { Plus, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ProductsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = async () => setRows((await api.get("/products")).data);
  useEffect(() => {
    load();
    // Initial fetch only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (form, id) => {
    try {
      if (id) await api.put(`/products/${id}`, form);
      else await api.post("/products", form);
      toast.success("Saved");
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };
  const uploadExcel = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported — created ${data.created}, updated ${data.updated}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had issues`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("download failed:", err);
      toast.error("Download failed");
    }
  };

  // Derive distinct categories for the filter dropdown
  const categories = Array.from(new Set(rows.map((r) => r.category).filter(Boolean))).sort();
  const filtered = rows.filter((r) => {
    if (categoryFilter && r.category !== categoryFilter) return false;
    if (!q) return true;
    const hay = `${r.code} ${r.name} ${r.packing_size} ${r.outer_packing} ${r.category}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle="Product catalogue — codes, packing, outer packing, unit and category. Upload your PRODUCTS LIST.xlsx directly."
        actions={
          <>
            <Button variant="outline" onClick={() => download("/products/template.xlsx", "products_template.xlsx")}>
              <Download className="w-4 h-4" /> Template
            </Button>
            <Button variant="outline" onClick={() => download("/products/export.xlsx", "products.xlsx")} data-testid="products-export">
              <Download className="w-4 h-4" /> Export
            </Button>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-transparent text-[#1E3F20] border border-[#1E3F20] hover:bg-[#F2EDE4] rounded-lg px-4 py-2 font-semibold text-sm">
              <Upload className="w-4 h-4" /> Upload
              <input type="file" accept=".xlsx" className="hidden" data-testid="products-upload" onChange={(e) => e.target.files?.[0] && uploadExcel(e.target.files[0])} />
            </label>
            <Button
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              data-testid="new-product-btn"
            >
              <Plus className="w-4 h-4" /> New product
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, code, packing…" className="max-w-sm" />
        {categories.length > 0 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-[#E5E2DC] bg-white rounded-lg px-3 py-2.5 text-[#1A2E1C] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent text-sm max-w-xs"
            data-testid="products-category-filter"
          >
            <option value="">All categories ({rows.length})</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c} ({rows.filter((r) => r.category === c).length})
              </option>
            ))}
          </select>
        )}
      </div>

      {open && (
        <ProductForm
          categories={categories}
          initial={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSave={(f) => save(f, editing?.id)}
        />
      )}

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No products" description="Add a product or upload your PRODUCTS LIST Excel file." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Packing</th>
                  <th className="px-4 py-3">Outer packing</th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60">
                    <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3">{r.packing_size}</td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{r.outer_packing}</td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{r.unit}</td>
                    <td className="px-4 py-3">
                      {r.category && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-[#A3B18A]/20 text-[#1E3F20]">
                          {r.category}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => {
                          setEditing(r);
                          setOpen(true);
                        }}
                        className="text-xs px-2.5 py-1 rounded hover:bg-[#F2EDE4] mr-1"
                      >
                        Edit
                      </button>
                      <button onClick={() => del(r.id)} className="text-red-600 p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ProductForm({ categories, initial, onClose, onSave }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    packing_size: "",
    outer_packing: "",
    unit: "Mts",
    category: "",
    ...(initial || {}),
  });
  return (
    <Card className="mb-6">
      <h3 className="font-heading text-xl font-bold mb-4">{initial ? "Edit" : "New"} product</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <div>
          <Label>Product code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. FGTGOMNM001" data-testid="product-code" />
        </div>
        <div>
          <Label>Product name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
        </div>
        <div>
          <Label>Packing</Label>
          <Input required value={form.packing_size} onChange={(e) => setForm({ ...form, packing_size: e.target.value })} placeholder="e.g. 1 Kg." data-testid="product-size" />
        </div>
        <div>
          <Label>Outer packing</Label>
          <Input value={form.outer_packing} onChange={(e) => setForm({ ...form, outer_packing: e.target.value })} placeholder="e.g. 25 Kg Bag" />
        </div>
        <div>
          <Label>Unit</Label>
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Mts / Pcs / Ltr" />
        </div>
        <div>
          <Label>Category</Label>
          <Input
            list="prod-cats"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="e.g. Micronutrient Fertilizers"
          />
          <datalist id="prod-cats">
            {(categories || []).map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" data-testid="product-submit">
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}
